// قالب‌بندی مبلغ — معادل MoneyFormatter سرور (ADR-013): Money(long AmountMinor, string Currency)؛ ریال عدد صحیح؛
// تومان فقط لایه نمایش (تقسیم بر ۱۰ و نمایش ریال باقیمانده به‌صورت اعشار تا هیچ ریالی پنهان نماند).
// مبلغ با bigint محاسبه می‌شود تا int64 سرور (که ممکن است به‌صورت رشته بیاید) بدون خطای float نمایش داده شود.
import { faDigits, groupThousands } from './digits';

export type MoneyDisplayUnit = 'Rial' | 'Toman';

/** تعداد رقم اعشار هر ارز (CurrencyCode.MinorUnitDigits سرور؛ پیش‌فرض ۲). */
export const MINOR_UNIT_DIGITS: Readonly<Record<string, number>> = {
  IRR: 0,
  IQD: 3,
  USD: 2,
  EUR: 2,
  AED: 2,
  TRY: 2,
};

export interface MoneyLabels {
  rial: string;
  toman: string;
}

export interface FormatMoneyOptions {
  /** ارقام فارسی (پیش‌فرض true). */
  persianDigits?: boolean;
  /** افزودن برچسب واحد (پیش‌فرض true). */
  includeUnitLabel?: boolean;
  /** برچسب‌های ترجمه‌شده ریال و تومان (از i18n: common:money.rial و common:money.toman). */
  labels?: MoneyLabels;
}

function toBigInt(amountMinor: number | bigint | string): bigint {
  if (typeof amountMinor === 'bigint') {
    return amountMinor;
  }
  if (typeof amountMinor === 'number') {
    if (!Number.isInteger(amountMinor)) {
      throw new RangeError('amountMinor must be an integer (ADR-013: no float for money).');
    }
    return BigInt(amountMinor);
  }
  return BigInt(amountMinor.trim());
}

function abs(value: bigint): bigint {
  return value < 0n ? -value : value;
}

/**
 * قالب‌بندی مبلغ: `formatMoney(1_250_000, 'IRR', 'Toman')` ← «۱۲۵٬۰۰۰ تومان».
 * واحد نمایش فقط برای ریال معنا دارد؛ سایر ارزها با رقم اعشار خودشان و کد ارز نمایش داده می‌شوند.
 */
export function formatMoney(
  amountMinor: number | bigint | string,
  currency = 'IRR',
  unit: MoneyDisplayUnit = 'Rial',
  options: FormatMoneyOptions = {},
): string {
  const { persianDigits = true, includeUnitLabel = true, labels } = options;
  const amount = toBigInt(amountMinor);
  const code = currency.toUpperCase();
  const isRial = code === 'IRR';

  let number: string;
  let label: string;

  if (isRial && unit === 'Toman') {
    const sign = amount < 0n ? '-' : '';
    const absolute = abs(amount);
    const remainder = absolute % 10n;
    number = sign + groupThousands(absolute / 10n);
    if (remainder !== 0n) {
      number += `.${remainder.toString()}`;
    }
    label = labels?.toman ?? 'toman';
  } else if (isRial) {
    number = groupThousands(amount);
    label = labels?.rial ?? 'rial';
  } else {
    const digits = MINOR_UNIT_DIGITS[code] ?? 2;
    const factor = 10n ** BigInt(digits);
    if (digits === 0) {
      number = groupThousands(amount);
    } else {
      const sign = amount < 0n ? '-' : '';
      const absolute = abs(amount);
      const minor = (absolute % factor).toString().padStart(digits, '0');
      number = `${sign}${groupThousands(absolute / factor)}.${minor}`;
    }
    label = code;
  }

  const text = includeUnitLabel ? `${number} ${label}` : number;
  return persianDigits ? faDigits(text) : text;
}
